import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiJson } from '@/api/client'
import type {
  BulkLinkToCasePayload,
  CaseSummary,
} from '@/features/alerts/types'
import {
  listGroupingSuggestions,
  unlinkAlerts,
} from '../api'
import type { UnlinkAlertsPayload } from '../types'

export const groupingSuggestionsKey = (alertId: string) =>
  ['cases', 'suggestions', alertId] as const

/**
 * AF-127 — Suggested groupings for an alert.
 *
 * Cached 60 seconds per the technical notes ("fetched on alert detail mount,
 * cached 60 seconds"). The "shared X" reason strings come from the backend —
 * we never re-derive them client-side.
 */
export function useGroupingSuggestions(alertId: string | undefined) {
  return useQuery({
    queryKey: groupingSuggestionsKey(alertId ?? ''),
    queryFn: () => listGroupingSuggestions(alertId!),
    enabled: alertId !== undefined && alertId !== '',
    staleTime: 60_000,
    select: (data) => data.suggestions,
  })
}

/**
 * AF-127 — Link alerts to a case (existing or new).
 *
 * Behaviour:
 *   - Posts to `/alerts/bulk/link-to-case`.
 *   - On success, invalidates the alerts and cases caches so the
 *     case-link badge appears on the row.
 *   - Surfaces an Undo toast for 10 seconds (per AC). The undo path calls
 *     `unlinkAlerts` — it's a real round-trip, not just a UI revert.
 */
export function useLinkToCase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: BulkLinkToCasePayload) =>
      apiJson<{ case_id: string; created: boolean; updated: number; skipped?: number }>(
        '/alerts/bulk/link-to-case',
        { method: 'POST', body: payload },
      ),
    onSuccess: (data, variables) => {
      void qc.invalidateQueries({ queryKey: ['alerts'] })
      void qc.invalidateQueries({ queryKey: ['cases'] })
      const updated = data.updated
      const skipped = data.skipped ?? 0
      const plural = (n: number) => (n === 1 ? 'alert' : 'alerts')
      const head = data.created
        ? `Created case · ${updated} ${plural(updated)} added`
        : `Added ${updated} ${plural(updated)} to case`
      const title = skipped > 0
        ? `${head} · ${skipped} skipped (already linked elsewhere)`
        : head
      toast.success(title, {
        duration: 10_000,
        action: {
          label: 'Undo',
          onClick: () => {
            void unlinkAlerts({
              case_id: data.case_id,
              alert_ids: variables.alert_ids,
              reason: 'Undo from link-to-case toast',
            }).then(() => {
              void qc.invalidateQueries({ queryKey: ['alerts'] })
              void qc.invalidateQueries({ queryKey: ['cases'] })
              toast.success('Reverted')
            })
          },
        },
      })
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Couldn't link to case")
    },
  })
}

/**
 * AF-127 — Unlink alerts from a case.
 *
 * `cases:unlink_large` is required server-side when the case has ≥5 alerts.
 * The UI also gates with `useCanAny('cases:unlink_large')` for ≥5-alert cases
 * to avoid a 403 round-trip. See `UnlinkButton`.
 */
export function useUnlinkAlerts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UnlinkAlertsPayload) => unlinkAlerts(payload),
    onSuccess: (data, variables) => {
      toast.success(
        `Unlinked ${data.unlinked} alert${data.unlinked === 1 ? '' : 's'}`,
      )
      void qc.invalidateQueries({ queryKey: ['cases', 'detail', variables.case_id] })
      void qc.invalidateQueries({ queryKey: ['alerts'] })
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Couldn't unlink alerts")
    },
  })
}

export type { CaseSummary }
